import { BetaSchemaForm, ProCard, type ProFormColumnsType } from "@ant-design/pro-components";
import { Alert, Button, Input, Space } from "antd";
import type {
  AlertProps,
  ButtonProps,
  FormInstance,
  GetProps,
  InputNumberProps,
  InputProps,
  RadioGroupProps,
  SliderSingleProps,
  SwitchProps,
} from "antd";
import { Fragment, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { t } from "@airange/shared/i18n-admin-controls";

type OptionValue = string | number | null;
type TextAreaProps = GetProps<typeof Input.TextArea>;

export type AdminFormOption = {
  label: ReactNode;
  value: OptionValue;
};

type BaseAdminFormFieldSchema = {
  key: string;
  label: ReactNode;
  name: string;
  required?: boolean;
  rules?: Array<{ required?: boolean; message?: string }>;
  width?: number | string;
  placeholder?: string;
  hidden?: boolean;
  readonly?: boolean;
  help?: ReactNode;
  extra?: ReactNode;
  action?: {
    label: ReactNode;
    onClick: () => void;
    className?: string;
  };
};

type InputAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "input";
  inputType?: string;
  inputProps?: InputProps;
};

type SelectAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "select";
  options: AdminFormOption[];
  onChange?: (value: OptionValue) => void;
  mode?: "multiple" | "tags";
};

type NumberAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "number";
  inputNumberProps?: InputNumberProps;
};

type RadioAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "radio";
  options: AdminFormOption[];
  radioProps?: RadioGroupProps;
};

type TextAreaAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "textarea";
  textAreaProps?: TextAreaProps;
};

type SwitchAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "switch";
  switchProps?: SwitchProps;
};

type SliderAdminFormFieldSchema = BaseAdminFormFieldSchema & {
  type: "slider";
  sliderProps?: SliderSingleProps;
};

export type AdminFormFieldSchema =
  | InputAdminFormFieldSchema
  | SelectAdminFormFieldSchema
  | NumberAdminFormFieldSchema
  | RadioAdminFormFieldSchema
  | TextAreaAdminFormFieldSchema
  | SwitchAdminFormFieldSchema
  | SliderAdminFormFieldSchema;

export type AdminFormSectionSchema = {
  key: string;
  title?: ReactNode;
  fields?: AdminFormFieldSchema[];
  render?: () => ReactNode;
  cardClassName?: string;
  bordered?: boolean;
};

export type AdminFormNoticeItem = Omit<AlertProps, "message"> & {
  key: string;
  message?: ReactNode;
};

export type AdminFormActionItem = {
  key: string;
  label: ReactNode;
  onClick?: () => void;
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

export const ADMIN_HORIZONTAL_FORM_PROPS = {
  layout: "horizontal" as const,
  labelAlign: "left" as const,
  labelWrap: true,
  labelCol: { flex: "180px" },
  wrapperCol: { flex: "min(720px, 100%)" },
};

export const ADMIN_HORIZONTAL_DIALOG_FORM_PROPS = {
  layout: "horizontal" as const,
  labelAlign: "left" as const,
  labelWrap: true,
  labelCol: { flex: "160px" },
  // Let dialog-form controls consume the remaining row width so label/input stay inline inside drawers and modals.
  wrapperCol: { flex: "1 1 0" },
};

export const ADMIN_SUBMIT_AND_CLOSE_FORM_PROPS = {
  clearOnDestroy: true,
} as const;

export const ADMIN_SUBMIT_AND_CLOSE_DRAWER_PROPS = {
  destroyOnHidden: true,
} as const;

type AdminSetFieldsValuePayload<TValues extends object> = Parameters<FormInstance<TValues>["setFieldsValue"]>[0];

export type AdminOpenFormSyncOptions<TValues extends object> = {
  form: FormInstance<TValues>;
  open: boolean;
  values: AdminSetFieldsValuePayload<TValues> | null | undefined;
  syncKey: string | number | boolean | null | undefined;
  enabled?: boolean;
  resetBeforeSync?: boolean;
};

export function useAdminOpenFormSync<TValues extends object>({
  form,
  open,
  values,
  syncKey,
  enabled = true,
  resetBeforeSync = true,
}: AdminOpenFormSyncOptions<TValues>) {
  const lastSyncedKeyRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (!open || !enabled || !values || syncKey == null) {
      if (!open) {
        lastSyncedKeyRef.current = undefined;
      }
      return;
    }

    if (Object.is(lastSyncedKeyRef.current, syncKey)) {
      return;
    }

    if (resetBeforeSync) {
      form.resetFields();
    }
    form.setFieldsValue(values);
    lastSyncedKeyRef.current = syncKey;
  }, [enabled, form, open, resetBeforeSync, syncKey, values]);
}

function renderAdminFormItemExtra(field: BaseAdminFormFieldSchema) {
  if (!field.extra && !field.help) {
    return undefined;
  }
  return (
    <div className="admin-form-item-extra-stack">
      {field.extra ? <div>{field.extra}</div> : null}
      {field.help ? <div>{field.help}</div> : null}
    </div>
  );
}

function getAdminFormFieldPlaceholder(field: AdminFormFieldSchema) {
  if (field.placeholder || field.readonly || field.hidden) {
    return field.placeholder;
  }
  if (field.type === "select") {
    return t("common.placeholders.select");
  }
  if (field.type === "input" || field.type === "textarea" || field.type === "number") {
    return t("common.placeholders.enter");
  }
  return undefined;
}

function toFieldValueType(field: AdminFormFieldSchema): string {
  switch (field.type) {
    case "textarea":
      return "textarea";
    case "number":
      return "digit";
    case "radio":
      return "radio";
    case "switch":
      return "switch";
    case "slider":
      return "slider";
    case "select":
      return "select";
    default:
      return "text";
  }
}

function buildFieldWidth(field: AdminFormFieldSchema) {
  if (typeof field.width === "number") {
    return { width: field.width };
  }
  if (typeof field.width === "string") {
    return { width: field.width };
  }
  if (field.type === "select" || field.type === "number") {
    return { width: "100%" };
  }
  return undefined;
}

function toSchemaColumn(field: AdminFormFieldSchema): ProFormColumnsType<Record<string, unknown>> {
  const placeholder = getAdminFormFieldPlaceholder(field);
  const fieldProps: Record<string, unknown> = {
    ...(buildFieldWidth(field) ?? {}),
  };
  const formItemProps: Record<string, unknown> = {
    extra: renderAdminFormItemExtra(field),
    className: "admin-form-item",
  };
  const rules = field.rules ?? (field.required ? [{ required: true }] : undefined);

  if (field.type === "input") {
    if (field.inputType) {
      fieldProps.type = field.inputType;
    }
    if (placeholder) {
      fieldProps.placeholder = placeholder;
    }
    if (field.readonly) {
      fieldProps.readOnly = true;
    }
    Object.assign(fieldProps, field.inputProps ?? {});
  } else if (field.type === "textarea") {
    if (placeholder) {
      fieldProps.placeholder = placeholder;
    }
    if (field.readonly) {
      fieldProps.readOnly = true;
    }
    Object.assign(fieldProps, field.textAreaProps ?? {});
  } else if (field.type === "number") {
    if (placeholder) {
      fieldProps.placeholder = placeholder;
    }
    if (field.readonly) {
      fieldProps.readOnly = true;
    }
    Object.assign(fieldProps, field.inputNumberProps ?? {});
  } else if (field.type === "select") {
    fieldProps.options = field.options;
    if (placeholder) {
      fieldProps.placeholder = placeholder;
    }
    if (field.mode) {
      fieldProps.mode = field.mode;
    }
    if (field.onChange) {
      fieldProps.onChange = field.onChange;
    }
  } else if (field.type === "radio") {
    fieldProps.options = field.options;
    Object.assign(fieldProps, field.radioProps ?? {});
  } else if (field.type === "switch") {
    Object.assign(fieldProps, field.switchProps ?? {});
    if (field.readonly || field.switchProps?.disabled) {
      fieldProps.disabled = true;
    }
  } else if (field.type === "slider") {
    Object.assign(fieldProps, field.sliderProps ?? {});
  }

  const column: ProFormColumnsType<Record<string, unknown>> = {
    key: field.key,
    title: field.label,
    dataIndex: field.name,
    valueType: toFieldValueType(field) as never,
    readonly: field.readonly,
    hidden: field.hidden,
    fieldProps,
    formItemProps: {
      ...formItemProps,
      rules,
    },
  } as ProFormColumnsType<Record<string, unknown>>;

  if (field.action) {
    const action = field.action;
    (column as { renderFormItem?: (...args: unknown[]) => ReactNode }).renderFormItem = (_schema, config, form) => {
      const defaultDom = (form as { defaultRender?: (...args: unknown[]) => ReactNode } | undefined)?.defaultRender?.(_schema, config, form);
      return (
        <div className="admin-inline-form-row admin-inline-form-row-full">
          <div className="admin-inline-form-grow">{defaultDom}</div>
          <Button className={`admin-button-nowrap ${action.className ?? ""}`.trim()} onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      );
    };
  }

  return column;
}

function renderSchemaFormColumns(fields: AdminFormFieldSchema[]) {
  return fields.map((field) => toSchemaColumn(field));
}

function SchemaSectionBody({ fields = [] }: { fields?: AdminFormFieldSchema[] }) {
  if (fields.length === 0) {
    return null;
  }
  return (
    <BetaSchemaForm<Record<string, unknown>>
      layoutType="Embed"
      submitter={false}
      columns={renderSchemaFormColumns(fields)}
    />
  );
}

export function renderAdminFormFields(fields: AdminFormFieldSchema[]) {
  return <SchemaSectionBody fields={fields} />;
}

export function AdminFormSection({ section }: { section: AdminFormSectionSchema }) {
  if (!section.title && section.bordered === false) {
    return (
      <div className={section.cardClassName ?? "admin-form-section admin-form-section--plain"}>
        <div className="admin-form-section__body">
          <SchemaSectionBody {...(section.fields ? { fields: section.fields } : {})} />
          {section.render ? section.render() : null}
        </div>
      </div>
    );
  }

  return (
    <ProCard
      title={section.title}
      variant="outlined"
      className={section.cardClassName ?? "admin-form-section"}
    >
      <div className="admin-form-section__body">
        <SchemaSectionBody {...(section.fields ? { fields: section.fields } : {})} />
        {section.render ? section.render() : null}
      </div>
    </ProCard>
  );
}

export function AdminFormNotice({ notices }: { notices: AdminFormNoticeItem[] }) {
  if (notices.length === 0) {
    return null;
  }
  return (
    <Space vertical size={12} className="admin-space-full">
      {notices.map(({ key, message, title, ...item }) => (
        <Alert key={key} {...item} title={title ?? message} />
      ))}
    </Space>
  );
}

export function AdminFormActions({ actions }: { actions: AdminFormActionItem[] }) {
  if (actions.length === 0) {
    return null;
  }
  return (
    <div className="admin-form-actions">
      <Space className="admin-form-action-space">
        {actions.map((action) => (
          <Button
            key={action.key}
            onClick={action.onClick}
            {...resolveAdminFormActionButtonVisual(action)}
            {...(action.danger === undefined ? {} : { danger: action.danger })}
            {...(action.disabled === undefined ? {} : { disabled: action.disabled })}
            {...(action.loading === undefined ? {} : { loading: action.loading })}
          >
            {action.label}
          </Button>
        ))}
      </Space>
    </div>
  );
}

function resolveAdminFormActionButtonVisual(action: AdminFormActionItem): Pick<ButtonProps, "color" | "variant"> {
  return {
    color: action.color ?? (action.danger ? "danger" : "default"),
    variant: action.variant ?? "outlined",
  };
}

export function AdminFormWorkspace({
  notices = [],
  sections,
  actions = [],
}: {
  notices?: AdminFormNoticeItem[];
  sections: AdminFormSectionSchema[];
  actions?: AdminFormActionItem[];
}) {
  return (
    <Space vertical size="middle" className="admin-space-full admin-form-workspace">
      <AdminFormNotice notices={notices} />
      <Space vertical size="middle" className="admin-space-full admin-form-workspace__sections">
        {sections.map((section) => (
          <Fragment key={section.key}>
            <AdminFormSection section={section} />
          </Fragment>
        ))}
      </Space>
      <AdminFormActions actions={actions} />
    </Space>
  );
}

export type FormFieldSchema = AdminFormFieldSchema;
export type FormSectionSchema = AdminFormSectionSchema;

export function SchemaFormFields({ fields }: { fields: FormFieldSchema[] }) {
  return <SchemaSectionBody fields={fields} />;
}

export function SchemaFormSections({ sections }: { sections: FormSectionSchema[] }) {
  return (
    <Space vertical size="middle" className="admin-space-full admin-exam-create-step">
      {sections.map((section) => (
        <Fragment key={section.key}>
          <ProCard
            className={section.cardClassName ?? "admin-surface-card"}
            title={section.title}
          >
            <SchemaSectionBody {...(section.fields ? { fields: section.fields } : {})} />
            {section.render ? section.render() : null}
          </ProCard>
        </Fragment>
      ))}
    </Space>
  );
}
